import { Barra, Carregando } from '@/componentes/Esqueleto';
import { Medida } from '@/componentes/Medida';
import { formatarDecimal, formatarDia } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { perfilAPedir } from '@/lib/perfil-da-licenca';
import { useSessao } from '@/modulos/sessao/consultas';

import { useTelemetriaDaMaquina } from './api';
import { quando, rotuloDoTipo, valorEscrito } from './telemetria';

/**
 * A telemetria na ficha da máquina (mesa): o dispositivo que está nela, quando
 * falou pela última vez, o último valor de cada campo e as horas de motor dos
 * últimos dias. Obedece à leitura do módulo 17: sem `telemetria.ler`, diz o
 * perfil a pedir. Sem dispositivo, diz que não há — nunca zero.
 */
export function TelemetriaNaFicha({ equipamentoId }: { readonly equipamentoId: string }) {
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('telemetria.ler');
  const t = useTelemetriaDaMaquina(equipamentoId, pode);
  if (sessao.data && !pode) {
    return (
      <span className="txt-apoio text-tinta-apagada">
        {`Seu perfil não vê o módulo de telemetria. Peça ao administrador da sua empresa o perfil ${perfilAPedir('telemetria.ler')}.`}
      </span>
    );
  }
  if (t.isError) return <span className="txt-apoio text-tinta-secundaria">{`A telemetria não carregou. ${explicarFalha(t.error).comoResolver}`}</span>;
  if (!t.data) {
    return (
      <Carregando oQue="Carregando a telemetria da máquina">
        <Barra className="my-1 h-3 w-1/2" />
        <Barra className="my-1 h-3 w-2/3" />
      </Carregando>
    );
  }
  const d = t.data;
  if (d.dispositivos.length === 0) {
    return <span className="txt-apoio text-tinta-apagada">Nenhum dispositivo de telemetria nesta máquina. As leituras vêm do apontamento, do abastecimento e do campo.</span>;
  }
  return (
    <div className="flex flex-col gap-2">
      {d.dispositivos.map((x) => (
        <span key={x.dispositivoId} className="txt-apoio text-tinta-secundaria">
          <span className="codigo text-tinta">{x.identificador}</span>
          {` · ${x.integracao} · último sinal ${quando(x.ultimoSinalEm)} · ${x.conferidoEm ? 'medidor conferido' : 'medidor não conferido: a leitura ainda não alimenta a máquina'}`}
        </span>
      ))}
      {d.ultimos.length > 0 ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
          {d.ultimos.map((u) => (
            <div key={u.tipo} className="flex flex-col">
              <dt className="txt-apoio text-tinta-secundaria">{rotuloDoTipo(u.tipo)}</dt>
              <dd className="txt-corpo tabular-nums text-tinta">{valorEscrito(u.tipo, u)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <span className="txt-apoio text-tinta-apagada">O dispositivo ainda não mandou leitura aceita.</span>
      )}
      {d.dias.length > 0 ? (
        <ul aria-label="Horas de motor da telemetria" className="flex flex-col">
          {d.dias.map((x) => (
            <li key={x.dia} className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 border-t border-borda-sutil py-1">
              <span className="txt-apoio text-tinta-secundaria">{`${formatarDia(x.dia)} · motor${x.horasOciosas !== null ? `, ${formatarDecimal(x.horasOciosas, 1)} h ociosas` : ''}`}</span>
              {x.horasMotor === null ? <span className="txt-apoio text-tinta-secundaria">—</span> : <Medida valor={formatarDecimal(x.horasMotor, 1)} unidade="h" />}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
